<template>
  <div class="shape-format">
    <h2 class="shape-format-title">形状属性</h2>
    <color-gradient-editor
      label="填充"
      showAlpha
      v-model="currentEffect.content.color"
      :gradient.sync="currentEffect.content.gradient"
      colorFormat="rgba"
      v-if="!currentEffect.content.shapeLinear"
    />
    <LabelSwitch label="阴影" v-model="currentEffect.content.shadow" />
    <template v-if="currentEffect.content.shadow">
      <div class="row-box">
        <LabelColor
          label="颜色"
          class="text-shadow-color"
          v-model="currentEffect.content.shadowColor"
          :showAlpha="false"
        />
        <LabelInput
          label="模糊"
          class="text-shadow-blurry"
          v-model="currentEffect.content.shadowBlur"
          :min="0"
        />
      </div>
      <div class="row-box">
        <div class="setting">
          <label>偏移</label>
        </div>
        <LabelInput
          label="x"
          class="text-shadow-offset-x"
          v-model="currentEffect.content.shadowX"
        />
        <LabelInput
          label="y"
          class="text-shadow-offset-y"
          v-model="currentEffect.content.shadowY"
        />
      </div>
    </template>
    <LabelSlider
      label="透明度"
      class="text-shadow-opacity"
      :value="effectInfo.opacity"
      type="opacity"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      v-model="currentEffect.content.rotate"
      :precision="0"
    />
    <flip-editor v-model="currentEffect.content.flip" />
    <LabelColor
      label="边框颜色"
      v-model="currentEffect.content.stroke"
      :showAlpha="false"
    />
    <LabelInput
      label="线条宽度"
      :min="1"
      :precision="0"
      v-model="displayLineWidth"
      v-if="currentEffect.content.shapeLinear"
    />
    <LabelInput
      label="边框宽度"
      :max="100"
      :min="0"
      :step="0.1"
      :precision="1"
      v-model="currentEffect.content.strokeWidth"
      :percentage="!currentEffect.content.strokeNumeric"
      v-else
    >
      <a-checkbox
        class="label-addon"
        v-model="currentEffect.content.strokeNumeric"
        >作为数值</a-checkbox
      >
    </LabelInput>
    <effect-param-editor
      v-for="desc in shapeDescs"
      :tag="desc.tag"
      :attrs="desc.attrs"
      v-model="currentEffect.content[desc.prop]"
      :key="desc.prop"
    />
    <position-editor :effect="currentEffect" />
    <label-button text="适应画布" @click="fitCanvas">大小</label-button>
    <a-button
      type="primary"
      block
      v-if="currentEffect.content.shape === 'path'"
      class="path-edit-toggle"
      @click="toggleEdit"
      >编辑路径</a-button
    >
    <template v-if="!currentEffect.container">
      <div class="border-bottom"></div>
      <h2 class="shape-format-title" style="margin-top: 12px">转场效果</h2>
      <LabelInput label="延迟出现（秒）" v-model="displayStart" :time="true" />
      <LabelInput
        label="显示时长（秒）"
        v-model="displayDuration"
        :time="true"
      />
      <LabelOption
        label="入场效果"
        :optionList="enterEffectList"
        v-model="currentEffect.enter"
      />
      <LabelInput
        label="入场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.enterDuration"
        :disabled="currentEffect.enter === 'none'"
      />
      <LabelOption
        label="出场效果"
        :optionList="leaveEffectList"
        v-model="currentEffect.leave"
      />
      <LabelInput
        label="出场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.leaveDuration"
        :disabled="currentEffect.leave === 'none'"
      />
      <div class="border-bottom"></div>

      <h2 class="shape-format-title" style="margin-top: 12px">动态效果</h2>
      <LabelSwitch
        :fn="fn"
        label="动态特效"
        v-model="currentEffect.showTracks"
      />
      <SingleEffectEditor
        label="特效"
        v-for="(track, i) in currentEffect.tracks"
        :disabled="!currentEffect.showTracks"
        :key="i"
        :track="track"
        :effectList="effectListAt(i)"
        @delete="removeTrack(i)"
        :deletable="currentEffect.tracks.length > 1"
      />
      <a-button
        v-if="currentEffect.tracks.length < 5"
        class="insert-track"
        @click="insertTrack"
        block
        :disabled="!currentEffect.showTracks"
        icon="plus"
        >继续添加</a-button
      >
    </template>
    <a-modal
      v-if="currentEffect.content.shape === 'path'"
      v-model="modalOpen"
      :title="false"
      @ok="handleOK"
      :footer="null"
    >
      <a-form @submit.prevent="done" :form="form">
        <a-tabs class="path-editor-tabs" :tabBarGutter="0" v-model="currentTab">
          <a-tab-pane key="1" tab="编辑">
            <p>
              编辑
              <a
                href="https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d"
                target="_blank"
              >
                <code>d</code> 属性
              </a>
            </p>
            <a-form-item>
              <a-textarea
                v-model="currentEffect.content.d"
                :autosize="{ minRows: 2 }"
              />
            </a-form-item>
          </a-tab-pane>
          <a-tab-pane key="2" tab="转换">
            <p>
              转换 DrawingML
              <a
                href="https://docs.microsoft.com/en-us/dotnet/api/documentformat.openxml.drawing.path?view=openxml-2.8.1"
                target="_blank"
              >
                <code>&lt;a:path&gt;</code> 元素
              </a>
            </p>
            <a-form-item>
              <a-textarea
                v-decorator="[
                  'input',
                  { rules: [{ required: true, message: 'Input something' }] }
                ]"
                :autosize="{ minRows: 2 }"
              />
            </a-form-item>
          </a-tab-pane>
        </a-tabs>
        <a-button type="primary" html-type="submit">完成</a-button>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { message } from 'ant-design-vue';
import LabelInput from "../base/LabelInput";
import LabelOption from "../base/LabelOption";
import LabelSwitch from "../base/LabelSwitch";
import LabelColor from "../base/LabelColor";
import LabelSlider from "../base/LabelSlider";
import LabelButton from '../base/LabelButton';
import EffectParamEditor from '../base/EffectParamEditor';
import SingleEffectEditor from '../base/SingleEffectEditor';
import PositionEditor from '../base/PositionEditor';
import FlipEditor from '../base/FlipEditor';
import ColorGradientEditor from '../base/ColorGradientEditor';
import { effectParams } from '../../utils/textEffectMap';
import { shapeParamDescs } from '../../utils/shapes';
import { fitCanvas } from '../../utils/util';

export default {
  name: "TextFormat",
  components: {
    LabelInput,
    LabelOption,
    LabelSwitch,
    LabelButton,
    LabelColor,
    LabelSlider,
    EffectParamEditor,
    SingleEffectEditor,
    PositionEditor,
    FlipEditor,
    ColorGradientEditor
  },
  data() {
    return {
      modalOpen: false,
      currentTab: '1',
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ],
      form: this.$form.createForm(this, { name: 'test' })
    };
  },
  computed: {
    ...mapGetters(['effectInfo', 'currentEffect', 'activeFragment', 'aspectRatio']),
    ...mapState(['effectNames', 'generalEffects']),
    fontList() {
      const { fontList } = this.$store.state;
      return fontList.map(item => ({
        name: item.font_name,
        value: item.font_name
      }));
    },
    shapeDescs() {
      return shapeParamDescs[this.currentEffect.content.shape] || [];
    },
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, 'start', Math.max(value, 0));
      }
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.$set(this.currentEffect, 'duration', Math.max(value, 400));
      }
    },
    displayLineWidth: {
      get() {
        return this.currentEffect.content.height * this.aspectRatio;
      },
      set(value) {
        this.$set(this.currentEffect.content, 'height', value / this.aspectRatio);
      }
    }
  },
  methods: {
    fn(showTracks) {
      if (showTracks) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    toggleEdit() {
      this.modalOpen = true;
    },
    done() {
      if (this.currentTab === '2') {
        this.form.validateFields((e, values) => {
          if (!e) {
            console.log(values);
            try {
              const dom = new DOMParser().parseFromString(`<Root xmlns:a="#">${values.input}</Root>`, 'application/xml');
              const pathElement = dom.getElementsByTagNameNS('#', 'path')[0];
              if (!pathElement) {
                throw new Error('No path element was found');
              }
              console.log(pathElement);
              const d = [].slice.call(pathElement.children).map(el => {
                switch (el.tagName) {
                  case 'a:moveTo': {
                    const child = el.children[0];
                    return `M${child.getAttribute('x')} ${child.getAttribute('y')}`;
                  }
                  case 'a:cubicBezTo': {
                    return `C${[].slice.call(el.children).map(child =>
                      `${child.getAttribute('x')} ${child.getAttribute('y')}`
                    ).join(' ')}`;
                  }
                  case 'a:lnTo': {
                    const child = el.children[0];
                    return `L${child.getAttribute('x')} ${child.getAttribute('y')}`;
                  }
                  case 'a:quadBezTo': {
                    return `Q${[].slice.call(el.children).map(child =>
                      `${child.getAttribute('x')} ${child.getAttribute('y')}`
                    ).join(' ')}`;
                  }
                  case 'a:arcTo': {
                    message.error('The a:arcTo element is not supported. The corresponding fragment is ignored in the generated path.');
                    return '';
                  }
                  case 'a:close': {
                    return 'z';
                  }
                }
              }).join('');
              this.$set(this.currentEffect.content, 'd', d);
              this.$set(this.currentEffect.content, 'shapeScale', 2 / pathElement.getAttribute('h'));
              this.$set(this.currentEffect.content, 'shapeY', -1);
              this.$set(this.currentEffect.content, 'shapeX', -1);
              this.form.resetFields();
              this.modalOpen = false;
            } catch (e) {
              message.error('请输入有效的路径。');
            }
          }
        });
      } else {
        this.modalOpen = false;
      }
    },
    /**
     * fit the image within the canvas' size.
     */
    fitCanvas() {
      const efc = this.currentEffect.content;
      const { top, left, width, height } = fitCanvas(efc, 480 * this.$store.state.screenRatio, 480);

      efc.height = height;
      efc.width = width;
      efc.top = top;
      efc.left = left;
    }
  }
}
</script>

<style scoped>
.shape-format {
  background: rgba(248, 248, 248, 1);
  padding: 13px;
  box-sizing: border-box;
}

.shape-format-title {
  font-size: 14px;
  font-weight: 500;
  /* color: #000; */
  margin: 0 0 10px 0;
}

.setting {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shape-format .border-bottom {
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(216, 216, 216, 1);
  width: 100%;
}

.path-edit-toggle {
  margin-top: 12px;
}

.row-box {
  font-size: 12px;
  color: #656667;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.path-editor-tabs {
  margin: -24px -24px 0;
}

.insert-track {
  border-color: #e5e5e7;
  background: #fff;
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.75);
  border-radius: 2px;
}

.insert-track:hover,
.insert-track:focus {
  border-color: #ccc;
}

.insert-track:disabled {
  border-color: #dcdcdc;
  background: #fff;
  color: rgba(0, 0, 0, 0.75);
  opacity: 0.35;
}

.label-addon {
  margin-left: 4px;
  font-size: 12px;
}

.label-addon ::v-deep .ant-checkbox + span {
  padding-right: 0;
}
</style>

<style>
.shape-format .setting-input {
  margin-left: 3px;
}

.shape-format .ant-radio-group {
  margin-left: 3px;
}

.path-editor-tabs .ant-tabs-tab {
  padding: 18px 24px;
}

.path-editor-tabs .ant-tabs-tabpane {
  padding: 0 24px;
}

.path-editor-tabs .ant-form-item {
  margin-bottom: 16px;
}
</style>
